/**
 * Strava upload flow (SPEC §11.2/11.3): POST the FIT via the Worker, poll until there is an activity id or an
 * error. Policy (default "ask"): Mario also records on his Edge → Garmin → Strava would create duplicates.
 */
import { db } from '@/db/db';
import type { RideRecord, StravaStatus } from '@/ride/model';
import { api } from './api';

export const STRAVA_ACTIVITY_URL = (id: number) => `https://www.strava.com/activities/${id}`;

async function setStatus(rideId: string, strava: StravaStatus): Promise<void> {
  await db.rides.update(rideId, { strava, updatedAt: Date.now() });
}

export function rideDescription(r: RideRecord): string {
  const s = r.summary;
  const parts = [
    `VeloStube indoor ride${r.routeName ? ` – ${r.routeName}` : ''}${r.workoutName ? ` – ${r.workoutName}` : ''}`,
    `NP ${Math.round(s.np)} W · IF ${s.if.toFixed(2)} · TSS ${Math.round(s.tss)}`,
  ];
  return parts.join('\n');
}

/** Strava's error for a ride it already has names the existing activity (as an HTML link or "activity <id>"). */
const DUPLICATE_RE = /duplicate of\b.*?(?:\/activities\/|activity\s+)(\d+)/i;

/** Strava upload error → status: a duplicate means the ride is on Strava; other messages lose Strava's HTML. */
export function stravaErrorStatus(message: string): StravaStatus {
  const dup = DUPLICATE_RE.exec(message);
  if (dup) return { state: 'done', activityId: Number(dup[1]) };
  return { state: 'error', message: message.replace(/<[^>]*>/g, '').trim() };
}

type UploadOpts = { pollMs?: number; maxPolls?: number; onStatus?: (s: StravaStatus) => void };

// One upload per ride at a time: the auto upload and the summary page's button must not both POST the FIT.
const inFlight = new Map<string, Promise<StravaStatus>>();

export function uploadRideToStrava(ride: RideRecord, opts: UploadOpts = {}): Promise<StravaStatus> {
  const running = inFlight.get(ride.id);
  if (running) return running;
  const p = upload(ride, opts).finally(() => inFlight.delete(ride.id));
  inFlight.set(ride.id, p);
  return p;
}

async function upload(ride: RideRecord, opts: UploadOpts): Promise<StravaStatus> {
  // The caller's record can be stale (the page loaded it before a background upload finished).
  const current = (await db.rides.get(ride.id))?.strava;
  if (current?.state === 'done') return current;
  const data = await db.rideData.get(ride.id);
  if (!data?.fit) {
    const st: StravaStatus = { state: 'error', message: 'Keine FIT-Datei vorhanden' };
    await setStatus(ride.id, st);
    return st;
  }
  const report = async (st: StravaStatus) => {
    await setStatus(ride.id, st);
    opts.onStatus?.(st);
    return st;
  };
  try {
    const up = await api.stravaUpload(ride.id, data.fit, ride.name, rideDescription(ride));
    await report({ state: 'uploading', uploadId: up.id });
    if (up.activity_id) return report({ state: 'done', activityId: up.activity_id });
    for (let i = 0; i < (opts.maxPolls ?? 30); i++) {
      await new Promise((r) => setTimeout(r, opts.pollMs ?? 2000));
      const st = await api.stravaUploadStatus(up.id, ride.id);
      if (st.error) return report(stravaErrorStatus(st.error));
      if (st.activity_id) return report({ state: 'done', activityId: st.activity_id });
    }
    return report({ state: 'error', message: 'Zeitüberschreitung beim Warten auf Strava' });
  } catch (err) {
    return report(stravaErrorStatus(err instanceof Error ? err.message : String(err)));
  }
}
